import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock3,
  MinusCircle,
  ShieldCheck,
} from 'lucide-react';

import type {
  BillIntelligenceEvaluation,
  BillIntelligenceFinding,
} from '@schemas/api';
import { formatDateTime } from '@utils/formatters';

interface BillIntelligencePanelProps {
  evaluation: BillIntelligenceEvaluation;
  onViewDetails?: () => void;
}

interface BillIntelligenceDetailsProps {
  evaluation: BillIntelligenceEvaluation;
}

const decisionStyles: Record<string, string> = {
  post: 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-100',
  review: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100',
  hold: 'border-red-200 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100',
};

const resultStyles: Record<string, string> = {
  pass: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200',
  fail: 'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200',
  warning: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200',
  pending: 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-200',
  skipped: 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300',
};

const findingBorderStyles: Record<string, string> = {
  pass: 'border-emerald-200 dark:border-emerald-900',
  fail: 'border-red-200 dark:border-red-900',
  warning: 'border-amber-200 dark:border-amber-900',
  pending: 'border-blue-200 dark:border-blue-900',
  skipped: 'border-gray-200 dark:border-gray-700',
};

const labelAcronyms: Record<string, string> = {
  ap: 'AP',
  erp: 'ERP',
  po: 'PO',
};

const formatLabel = (value: string) => value
  .replace(/_/g, ' ')
  .split(' ')
  .map((word) => labelAcronyms[word.toLowerCase()] ?? `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
  .join(' ');

const summaryNumber = (
  evaluation: BillIntelligenceEvaluation,
  key: string,
  fallback: number,
) => {
  const value = evaluation.summary[key];
  return typeof value === 'number' ? value : fallback;
};

function FindingIcon({ result }: { result: string }) {
  if (result === 'pass') return <CheckCircle2 className="h-5 w-5 text-emerald-600" />;
  if (result === 'fail') return <AlertCircle className="h-5 w-5 text-red-600" />;
  if (result === 'pending') return <Clock3 className="h-5 w-5 text-blue-600" />;
  if (result === 'skipped') return <MinusCircle className="h-5 w-5 text-gray-500" />;
  return <AlertTriangle className="h-5 w-5 text-amber-600" />;
}

function EvidenceValue({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-gray-500 dark:text-gray-400">Not available</span>;
  }

  if (typeof value === 'boolean') {
    return <span>{value ? 'Yes' : 'No'}</span>;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return <span className="break-words">{String(value)}</span>;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-gray-500 dark:text-gray-400">None</span>;
    }

    if (value.every((item) => typeof item !== 'object' || item === null)) {
      return (
        <div className="flex flex-wrap gap-1.5">
          {value.map((item, index) => (
            <span
              key={`${String(item)}-${index}`}
              className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-200"
            >
              {formatLabel(String(item))}
            </span>
          ))}
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {value.map((item, index) => (
          <div
            key={index}
            className="rounded-md border border-gray-200 bg-white p-2.5 dark:border-gray-700 dark:bg-gray-900"
          >
            <EvidenceValue value={item} />
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([key]) => key !== 'id' && !key.endsWith('_id'));
    if (entries.length === 0) {
      return <span className="text-gray-500 dark:text-gray-400">Recorded</span>;
    }
    return (
      <dl className="grid gap-2 sm:grid-cols-2">
        {entries.map(([key, nestedValue]) => (
          <div key={key} className={key === 'description' ? 'sm:col-span-2' : undefined}>
            <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {formatLabel(key)}
            </dt>
            <dd className="mt-0.5 text-xs text-gray-700 dark:text-gray-200">
              <EvidenceValue value={nestedValue} />
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  return <span>{String(value)}</span>;
}

function FindingEvidence({ finding }: { finding: BillIntelligenceFinding }) {
  const entries = Object.entries(finding.evidence || {})
    .filter(([key]) => key !== 'id' && !key.endsWith('_id'));
  if (entries.length === 0) return null;

  return (
    <div className="mt-3 border-t border-gray-100 pt-3 dark:border-gray-700">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        Evidence
      </p>
      <dl className="grid gap-3 sm:grid-cols-2">
        {entries.map(([key, value]) => (
          <div key={key} className={key === 'candidates' || key === 'variances' || key === 'details' ? 'sm:col-span-2' : undefined}>
            <dt className="text-xs font-medium text-gray-600 dark:text-gray-300">
              {formatLabel(key)}
            </dt>
            <dd className="mt-1 text-sm text-gray-800 dark:text-gray-100">
              <EvidenceValue value={value} />
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function SummaryMetric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">{value}</p>
    </div>
  );
}

export function BillIntelligencePanel({ evaluation, onViewDetails }: BillIntelligencePanelProps) {
  const confidence = Math.round(Number(evaluation.confidence || 0) * 100);
  const controlCount = summaryNumber(evaluation, 'control_count', evaluation.findings.length);
  const blockingCount = summaryNumber(
    evaluation,
    'blocking_count',
    evaluation.findings.filter((finding) => finding.blocking).length,
  );
  const attentionCount = summaryNumber(
    evaluation,
    'attention_count',
    evaluation.findings.filter((finding) => finding.result === 'warning' || finding.result === 'pending').length,
  );

  return (
    <section className={`rounded-lg border p-4 ${decisionStyles[evaluation.decision] ?? decisionStyles.review}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <h3 className="font-semibold">Bill Intelligence</h3>
            <p className="mt-0.5 text-xs opacity-80">
              {evaluation.mode === 'verified' ? 'Verified invoice snapshot' : 'Provisional extracted snapshot'}
              {' · '}{confidence}% controls confidence
            </p>
          </div>
        </div>
        <span className="rounded-full border border-current/20 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide">
          {evaluation.decision}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-current/10 pt-3">
        <p className="text-sm opacity-90">
          {controlCount} controls · {blockingCount} blocking · {attentionCount} to review
        </p>
        {onViewDetails && (
          <button
            type="button"
            onClick={onViewDetails}
            className="inline-flex items-center gap-1.5 rounded-md border border-current/20 px-2.5 py-1.5 text-xs font-semibold hover:bg-white/40 focus:outline-none focus:ring-2 focus:ring-current/30 dark:hover:bg-black/10"
          >
            View details
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </section>
  );
}

export function BillIntelligenceDetails({ evaluation }: BillIntelligenceDetailsProps) {
  const confidence = Math.round(Number(evaluation.confidence || 0) * 100);
  const blockingCount = summaryNumber(
    evaluation,
    'blocking_count',
    evaluation.findings.filter((finding) => finding.blocking).length,
  );
  const attentionCount = summaryNumber(
    evaluation,
    'attention_count',
    evaluation.findings.filter((finding) => finding.result === 'warning' || finding.result === 'pending').length,
  );
  const passCount = summaryNumber(
    evaluation,
    'pass_count',
    evaluation.findings.filter((finding) => finding.result === 'pass').length,
  );

  return (
    <div className="space-y-5">
      <section className={`rounded-lg border p-5 ${decisionStyles[evaluation.decision] ?? decisionStyles.review}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0" />
            <div>
              <h3 className="text-lg font-semibold">Bill Intelligence report</h3>
              <p className="mt-1 text-sm opacity-80">
                {evaluation.mode === 'verified'
                  ? 'Verified against the approved invoice and currently available matching evidence.'
                  : 'Provisional controls based on the extracted invoice before final approval.'}
              </p>
              <p className="mt-2 text-xs opacity-70">
                Evaluated {formatDateTime(evaluation.created_at)} · Triggered by {formatLabel(evaluation.trigger)}
              </p>
            </div>
          </div>
          <span className="rounded-full border border-current/20 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide">
            {evaluation.decision}
          </span>
        </div>
      </section>

      <section aria-label="Bill Intelligence summary" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryMetric label="Control confidence" value={`${confidence}%`} />
        <SummaryMetric label="Passed" value={passCount} />
        <SummaryMetric label="Needs attention" value={attentionCount} />
        <SummaryMetric label="Blocking" value={blockingCount} />
      </section>

      <section>
        <div className="mb-3">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Control results</h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Each result is calculated by the backend from the persisted invoice snapshot.
          </p>
        </div>
        <div className="space-y-3">
          {evaluation.findings.map((finding) => (
            <article
              key={finding.id}
              className={`rounded-lg border bg-white p-4 dark:bg-gray-800 ${findingBorderStyles[finding.result] ?? findingBorderStyles.warning}`}
            >
              <div className="flex items-start gap-3">
                <FindingIcon result={finding.result} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {formatLabel(finding.category)}
                      </p>
                      <h4 className="mt-0.5 font-semibold text-gray-900 dark:text-gray-100">
                        {finding.title}
                      </h4>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {finding.blocking && (
                        <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-800 dark:bg-red-900 dark:text-red-100">
                          Blocks approval
                        </span>
                      )}
                      <span className={`rounded-full border px-2 py-1 text-xs font-semibold ${resultStyles[finding.result] ?? resultStyles.warning}`}>
                        {formatLabel(finding.result)}
                      </span>
                    </div>
                  </div>
                  <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                    {finding.explanation}
                  </p>
                  <FindingEvidence finding={finding} />
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <details className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <summary className="cursor-pointer text-sm font-semibold text-gray-900 dark:text-gray-100">
          Technical snapshot details
        </summary>
        <dl className="mt-4 grid gap-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Engine version</dt>
            <dd className="mt-1 text-gray-900 dark:text-gray-100">{evaluation.engine_version}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Evaluation mode</dt>
            <dd className="mt-1 capitalize text-gray-900 dark:text-gray-100">{evaluation.mode}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Snapshot fingerprint</dt>
            <dd className="mt-1 break-all font-mono text-xs text-gray-700 dark:text-gray-300">
              {evaluation.input_hash}
            </dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-xs font-medium text-gray-500 dark:text-gray-400">Rule versions</dt>
            <dd className="mt-2 grid gap-2 sm:grid-cols-2">
              {Object.entries(evaluation.rule_versions).map(([rule, version]) => (
                <div key={rule} className="rounded-md bg-gray-50 px-3 py-2 dark:bg-gray-900">
                  <span className="font-medium text-gray-800 dark:text-gray-200">{formatLabel(rule)}</span>
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{version}</span>
                </div>
              ))}
            </dd>
          </div>
        </dl>
      </details>
    </div>
  );
}
